export interface EmailTemplate {
  id: string;
  name: string;
  type: 
    | 'userInvitation' 
    | 'passwordReset' 
    | 'notification' 
    | 'welcome' 
    | 'roleChanged' 
    | 'accountDeactivation' 
    | 'taskAssigned'
    | 'taskCompleted'
    | 'taskOverdue'
    | 'projectCreated'
    | 'projectUpdated'
    | 'formSubmitted'
    | 'appointmentCreated'
    | 'appointmentUpdated'
    | 'custom';
  subject: string;
  htmlContent: string;
  textContent?: string;
  variables: string[]; // Available template variables like {{userName}}, {{organizationName}}, etc.
  description?: string;
  isActive: boolean;
  isDefault: boolean; // Whether this is the default template for this type
  organizationId?: string; // If null, it's a global template
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

export interface EmailTemplateVariable {
  name: string;
  description: string;
  required: boolean;
  example: string;
}

export const defaultEmailVariables: Record<string, EmailTemplateVariable[]> = {
  userInvitation: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'organizationName', description: 'Szervezet neve', required: true, example: 'Példa Kft.' },
    { name: 'userRole', description: 'Felhasználó szerepkör', required: true, example: 'Adminisztrátor' },
    { name: 'loginUrl', description: 'Bejelentkezési URL', required: true, example: 'https://youcrm.app' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  passwordReset: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'resetUrl', description: 'Jelszó visszaállítási URL', required: true, example: 'https://youcrm.app/reset' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  notification: [
    { name: 'subject', description: 'Értesítés címe', required: true, example: 'Fontos értesítés' },
    { name: 'message', description: 'Értesítés szövege', required: true, example: 'Az Ön fiókja frissítve lett.' },
    { name: 'organizationName', description: 'Szervezet neve', required: false, example: 'Példa Kft.' },
    { name: 'actionUrl', description: 'Műveleti URL', required: false, example: 'https://youcrm.app/action' },
    { name: 'actionText', description: 'Műveleti gomb szövege', required: false, example: 'Megtekintés' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  welcome: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'organizationName', description: 'Szervezet neve', required: true, example: 'Példa Kft.' },
    { name: 'loginUrl', description: 'Bejelentkezési URL', required: true, example: 'https://youcrm.app' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  roleChanged: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'organizationName', description: 'Szervezet neve', required: true, example: 'Példa Kft.' },
    { name: 'oldRole', description: 'Korábbi szerepkör', required: true, example: 'Felhasználó' },
    { name: 'newRole', description: 'Új szerepkör', required: true, example: 'Adminisztrátor' },
    { name: 'loginUrl', description: 'Bejelentkezési URL', required: true, example: 'https://youcrm.app' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  accountDeactivation: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'organizationName', description: 'Szervezet neve', required: true, example: 'Példa Kft.' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  custom: [
    { name: 'userName', description: 'Felhasználó neve', required: false, example: 'Kovács János' },
    { name: 'organizationName', description: 'Szervezet neve', required: false, example: 'Példa Kft.' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  taskAssigned: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'taskTitle', description: 'Feladat címe', required: true, example: 'Dokumentáció írása' },
    { name: 'taskNumber', description: 'Feladat száma', required: true, example: 'TASK-001' },
    { name: 'taskDescription', description: 'Feladat leírása', required: false, example: 'A projekt dokumentációjának elkészítése' },
    { name: 'projectName', description: 'Projekt neve', required: false, example: 'Website fejlesztés' },
    { name: 'priority', description: 'Prioritás', required: true, example: 'Magas' },
    { name: 'dueDate', description: 'Határidő', required: false, example: '2024.01.20' },
    { name: 'assignedBy', description: 'Ki rendelte hozzá', required: true, example: 'Nagy Péter' },
    { name: 'actionUrl', description: 'Feladat URL', required: true, example: 'https://youcrm.app/tasks/123' },
    { name: 'organizationName', description: 'Szervezet neve', required: true, example: 'Példa Kft.' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  taskCompleted: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'taskTitle', description: 'Feladat címe', required: true, example: 'Dokumentáció írása' },
    { name: 'taskNumber', description: 'Feladat száma', required: true, example: 'TASK-001' },
    { name: 'completedBy', description: 'Ki fejezte be', required: true, example: 'Nagy Péter' },
    { name: 'completionDate', description: 'Befejezés dátuma', required: true, example: '2024.01.18' },
    { name: 'actualHours', description: 'Tényleges órák', required: false, example: '8' },
    { name: 'actionUrl', description: 'Feladat URL', required: true, example: 'https://youcrm.app/tasks/123' },
    { name: 'organizationName', description: 'Szervezet neve', required: true, example: 'Példa Kft.' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  taskOverdue: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'taskTitle', description: 'Feladat címe', required: true, example: 'Dokumentáció írása' },
    { name: 'taskNumber', description: 'Feladat száma', required: true, example: 'TASK-001' },
    { name: 'dueDate', description: 'Határidő', required: true, example: '2024.01.10' },
    { name: 'daysOverdue', description: 'Lejárt napok száma', required: true, example: '5' },
    { name: 'actionUrl', description: 'Feladat URL', required: true, example: 'https://youcrm.app/tasks/123' },
    { name: 'organizationName', description: 'Szervezet neve', required: true, example: 'Példa Kft.' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  projectCreated: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'projectName', description: 'Projekt neve', required: true, example: 'Website fejlesztés' },
    { name: 'projectDescription', description: 'Projekt leírása', required: false, example: 'Új céges weboldal készítése' },
    { name: 'startDate', description: 'Kezdés dátuma', required: false, example: '2024.01.15' },
    { name: 'endDate', description: 'Befejezés dátuma', required: false, example: '2024.03.15' },
    { name: 'createdBy', description: 'Ki hozta létre', required: true, example: 'Nagy Péter' },
    { name: 'actionUrl', description: 'Projekt URL', required: true, example: 'https://youcrm.app/projects/123' },
    { name: 'organizationName', description: 'Szervezet neve', required: true, example: 'Példa Kft.' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  projectUpdated: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'projectName', description: 'Projekt neve', required: true, example: 'Website fejlesztés' },
    { name: 'changes', description: 'Változások leírása', required: true, example: 'Határidő módosítva' },
    { name: 'updatedBy', description: 'Ki módosította', required: true, example: 'Nagy Péter' },
    { name: 'actionUrl', description: 'Projekt URL', required: true, example: 'https://youcrm.app/projects/123' },
    { name: 'organizationName', description: 'Szervezet neve', required: true, example: 'Példa Kft.' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  formSubmitted: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'formName', description: 'Űrlap neve', required: true, example: 'Kapcsolatfelvételi űrlap' },
    { name: 'submitterName', description: 'Beküldő neve', required: false, example: 'Kiss Anna' },
    { name: 'submitterEmail', description: 'Beküldő email címe', required: false, example: 'anna@example.com' },
    { name: 'actionUrl', description: 'Űrlap válasz URL', required: true, example: 'https://youcrm.app/forms/responses/123' },
    { name: 'organizationName', description: 'Szervezet neve', required: true, example: 'Példa Kft.' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  appointmentCreated: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'clientName', description: 'Ügyfél neve', required: true, example: 'Kiss Anna' },
    { name: 'clientEmail', description: 'Ügyfél email címe', required: true, example: 'anna@example.com' },
    { name: 'appointmentDate', description: 'Időpont dátuma', required: true, example: '2024.01.20' },
    { name: 'appointmentTime', description: 'Időpont időpontja', required: true, example: '14:00' },
    { name: 'duration', description: 'Időtartam', required: false, example: '60 perc' },
    { name: 'location', description: 'Helyszín', required: false, example: 'Iroda - 2. emelet' },
    { name: 'actionUrl', description: 'Időpont URL', required: true, example: 'https://youcrm.app/appointments/123' },
    { name: 'organizationName', description: 'Szervezet neve', required: true, example: 'Példa Kft.' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ],
  appointmentUpdated: [
    { name: 'userName', description: 'Felhasználó neve', required: true, example: 'Kovács János' },
    { name: 'clientName', description: 'Ügyfél neve', required: true, example: 'Kiss Anna' },
    { name: 'appointmentDate', description: 'Új időpont dátuma', required: true, example: '2024.01.22' },
    { name: 'appointmentTime', description: 'Új időpont időpontja', required: true, example: '15:00' },
    { name: 'changes', description: 'Változások leírása', required: true, example: 'Időpont módosítva' },
    { name: 'actionUrl', description: 'Időpont URL', required: true, example: 'https://youcrm.app/appointments/123' },
    { name: 'organizationName', description: 'Szervezet neve', required: true, example: 'Példa Kft.' },
    { name: 'date', description: 'Aktuális dátum', required: false, example: '2024.01.15' }
  ]
};

export const defaultEmailTemplates: Partial<EmailTemplate>[] = [
  {
    name: 'Felhasználó meghívó',
    type: 'userInvitation',
    subject: 'Meghívó - {{organizationName}} YouCRM rendszer',
    htmlContent: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #2563eb; margin-bottom: 10px;">YouCRM</h1>
          <p style="color: #6b7280; font-size: 16px;">Üzleti kapcsolatok kezelése</p>
        </div>
        
        <div style="background: #f8fafc; padding: 30px; border-radius: 8px; margin-bottom: 30px;">
          <h2 style="color: #1f2937; margin-bottom: 20px;">Meghívó érkezett!</h2>
          <p style="font-size: 16px; line-height: 1.6; margin-bottom: 15px;">
            Kedves <strong>{{userName}}</strong>!
          </p>
          <p style="font-size: 16px; line-height: 1.6; margin-bottom: 15px;">
            Meghívást kapott a <strong>{{organizationName}}</strong> szervezet YouCRM rendszerébe.
          </p>
          <p style="font-size: 16px; line-height: 1.6; margin-bottom: 25px;">
            Szerepkör: <span style="background: #dbeafe; color: #1d4ed8; padding: 4px 8px; border-radius: 4px;">{{userRole}}</span>
          </p>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{loginUrl}}" style="background: #2563eb; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; font-weight: bold; display: inline-block;">
              Bejelentkezés a rendszerbe
            </a>
          </div>
        </div>
        
        <div style="background: #fef3c7; border: 1px solid #f59e0b; padding: 20px; border-radius: 6px; margin-bottom: 30px;">
          <h3 style="color: #92400e; margin-bottom: 10px;">Első bejelentkezés</h3>
          <p style="color: #92400e; font-size: 14px; line-height: 1.5;">
            Az első bejelentkezéskor kérjük használja a Google fiókját, amit ehhez az email címhez regisztrált.
            A rendszergazda már beállította az Ön jogosultságait.
          </p>
        </div>
        
        <div style="border-top: 1px solid #e5e7eb; padding-top: 20px; text-align: center; color: #6b7280; font-size: 14px;">
          <p>Üdvözlettel,<br><strong>{{organizationName}} csapata</strong></p>
          <p style="margin-top: 20px;">
            Ha nem Ön kérte ezt a meghívót, kérjük hagyja figyelmen kívül ezt az emailt.
          </p>
        </div>
      </div>
    `,
    variables: ['userName', 'organizationName', 'userRole', 'loginUrl', 'date'],
    description: 'Alapértelmezett meghívó template új felhasználók számára',
    isActive: true,
    isDefault: true
  },
  {
    name: 'Jelszó visszaállítás',
    type: 'passwordReset',
    subject: 'Jelszó visszaállítás - YouCRM',
    htmlContent: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #2563eb; margin-bottom: 10px;">YouCRM</h1>
          <p style="color: #6b7280; font-size: 16px;">Jelszó visszaállítás</p>
        </div>
        
        <div style="background: #f8fafc; padding: 30px; border-radius: 8px; margin-bottom: 30px;">
          <h2 style="color: #1f2937; margin-bottom: 20px;">Jelszó visszaállítása</h2>
          <p style="font-size: 16px; line-height: 1.6; margin-bottom: 15px;">
            Kedves <strong>{{userName}}</strong>!
          </p>
          <p style="font-size: 16px; line-height: 1.6; margin-bottom: 25px;">
            Jelszó visszaállítási kérést kaptunk az Ön fiókjához. Kattintson az alábbi gombra a jelszó módosításához:
          </p>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{resetUrl}}" style="background: #dc2626; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; font-weight: bold; display: inline-block;">
              Jelszó visszaállítása
            </a>
          </div>
          
          <p style="font-size: 14px; color: #6b7280; text-align: center;">
            Ez a link 24 órán belül lejár.
          </p>
        </div>
        
        <div style="background: #fef2f2; border: 1px solid #dc2626; padding: 20px; border-radius: 6px; margin-bottom: 30px;">
          <h3 style="color: #dc2626; margin-bottom: 10px;">Biztonsági figyelmeztetés</h3>
          <p style="color: #dc2626; font-size: 14px; line-height: 1.5;">
            Ha nem Ön kérte a jelszó visszaállítást, kérjük hagyja figyelmen kívül ezt az emailt. 
            Az Ön fiókja biztonságban marad.
          </p>
        </div>
        
        <div style="border-top: 1px solid #e5e7eb; padding-top: 20px; text-align: center; color: #6b7280; font-size: 14px;">
          <p>Üdvözlettel,<br><strong>YouCRM csapata</strong></p>
        </div>
      </div>
    `,
    variables: ['userName', 'resetUrl', 'date'],
    description: 'Alapértelmezett jelszó visszaállítási template',
    isActive: true,
    isDefault: true
  },
  {
    name: 'Általános értesítés',
    type: 'notification',
    subject: '{{subject}} - YouCRM értesítés',
    htmlContent: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #2563eb; margin-bottom: 10px;">YouCRM</h1>
          <p style="color: #6b7280; font-size: 16px;">{{organizationName}}</p>
        </div>
        
        <div style="background: #f8fafc; padding: 30px; border-radius: 8px; margin-bottom: 30px;">
          <h2 style="color: #1f2937; margin-bottom: 20px;">{{subject}}</h2>
          <div style="font-size: 16px; line-height: 1.6;">
            {{{message}}}
          </div>
          
          {{#if actionUrl}}
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{actionUrl}}" style="background: #2563eb; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; font-weight: bold; display: inline-block;">
              {{actionText}}
            </a>
          </div>
          {{/if}}
        </div>
        
        <div style="border-top: 1px solid #e5e7eb; padding-top: 20px; text-align: center; color: #6b7280; font-size: 14px;">
          <p>Üdvözlettel,<br><strong>{{organizationName}} csapata</strong></p>
          <p style="margin-top: 20px; font-size: 12px;">
            Dátum: {{date}}
          </p>
        </div>
      </div>
    `,
    variables: ['subject', 'message', 'organizationName', 'actionUrl', 'actionText', 'date'],
    description: 'Általános értesítési template',
    isActive: true,
    isDefault: true
  }
]; 